import React from 'react';
import { View, Text } from 'react-native';
import { ColumnMetadata, CellRendererProps } from '@components/settings/components/common/animatedTable/types';
import { NCFileBatchEvent } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { machineStatusColorName } from '@helpers';
import i18n from 'i18n-js';
import { RawMachineEventData } from '@interfaces/base';
import {
  DurationCell,
  StartTimeCell,
  VersionCell,
  QuantityDeltaCell,
  BatchCell,
  NCFileNameCell,
  MachineCell,
  QuantityCell,
  BarChartCell,
  StatusCell,
} from './cells';
import AccordionTriggerIconCell from '@components/settings/components/common/animatedTable/components/AccordionTriggerIconCell';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import EventTimeDisplay from './EventTimeDisplay';
import EventStatusSubStatusExecMode from './EventStatusSubStatusExecMode';
import { getStartBlockText } from '@v2/pages/common/components/NCFileBatchEvents/helpers';

export const NCFileBatchEventsCellRenderer = (
  column: ColumnMetadata,
  item: NCFileBatchEvent,
  { C, font, scale }: CellRendererProps
): React.ReactElement | null => {
  switch (column.type) {
    case 'status':
      return (
        <StatusCell event={item} C={C} font={font} scale={scale} includeExtraInfoBasedOnNCFileStatus={column.includeExtraInfoBasedOnNCFileStatus} />
      );

    case 'barChart':
      return (
        <BarChartCell
          event={item}
          eventType={column.eventType}
          eventsPerspective={column.eventsPerspective}
          C={C}
          scale={scale}
          maxTimeInSec={column.maxTimeInSec}
          idealTimeInSec={column.idealTimeInSec}
          paidTimeInSec={column.paidTimeInSec}
          actualTimeInSec={column.actualTimeInSec}
          scheduledTimeInSec={column.scheduledTimeInSec}
          showDots={column.showDots}
          firstElementKeyValue={column.firstElementKeyValue}
        />
      );

    case 'duration':
      return <DurationCell event={item} eventType={column.eventType} textConfig={column.textConfig} C={C} scale={scale} />;

    case 'startTime':
      return <StartTimeCell event={item} eventType={column.eventType} textConfig={column.textConfig} C={C} font={font} />;

    case 'version':
      return <VersionCell event={item} C={C} font={font} />;

    case 'quantityDelta':
      return <QuantityDeltaCell event={item} C={C} font={font} />;

    case 'quantity':
      return <QuantityCell event={item} C={C} />;

    case 'batch':
      return (
        <BatchCell
          event={item}
          eventType={column.eventType}
          textConfig={column.textConfig}
          C={C}
          font={font}
          isMultiOrSingleMachinePerspective={column.isMultiOrSingleMachinePerspective}
        />
      );

    case 'ncFileName':
      return <NCFileNameCell event={item} C={C} font={font} />;

    case 'machine':
      return <MachineCell event={item} eventType={column.eventType} textConfig={column.textConfig} C={C} font={font} />;

    case 'accordionTrigger': {
      const rowKey = column.getRowKey ? column.getRowKey(item) : item.sequenceNumber;
      const rowState = column.getRowState ? column.getRowState(rowKey) : null;
      const isExpandedValue = typeof column.isExpanded === 'function' ? column.isExpanded(item) : column.isExpanded;

      // If closing, immediately show as collapsed for instant visual feedback
      const shouldShowAsExpanded = rowState?.isClosing ? false : isExpandedValue;

      return <AccordionTriggerIconCell isExpanded={shouldShowAsExpanded} scale={scale} sequenceNumber={item.sequenceNumber} />;
    }

    // Subrow-specific cell types
    case 'subrow-status':
      return (
        <View>
          <EventStatusSubStatusExecMode {...{ C, event: item, font, scale }} />
        </View>
      );

    case 'subrow-duration': {
      const timeValue = column.textConfig?.duration?.[NcFileBatchEventType.TRACE]?.(item) ?? 0;
      return (
        <TimeDisplayInStaticFormat
          timeValue={timeValue}
          C={C}
          color={machineStatusColorName[item?.status]}
          fontForDigit={'Time 3'}
          fontForSecondDigit={'Time 4'}
          fontForDelimiter={'Caption 1'}
        />
      );
    }

    case 'subrow-start': {
      const startField = column.textConfig?.start?.[NcFileBatchEventType.TRACE]?.(item);
      if (!startField) return null;
      return <EventTimeDisplay timeValue={startField ?? 0} C={C} font={font} />;
    }

    case 'subrow-version': {
      const ncFileVersionData = item?.ncFileVersionSequenceNumbers || item?.ncFileVersionSequenceNumber;
      if (ncFileVersionData == null) return null;
      const ncFileVersion = Array.isArray(ncFileVersionData) ? ncFileVersionData[0] : ncFileVersionData;
      if (ncFileVersion == null) return null;
      return (
        <View>
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>v{ncFileVersion}</Text>
        </View>
      );
    }

    case 'subrow-quantityDelta': {
      const quantityDelta = item?.quantityDelta;
      if (!quantityDelta) return null;
      const shouldDisplayPlus = quantityDelta > 0;
      return (
        <View>
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
            {shouldDisplayPlus && '+'}
            {quantityDelta}
          </Text>
        </View>
      );
    }

    case 'subrow-countingMethod': {
      const quantityDelta = item?.quantityDelta;
      if (!quantityDelta) return null;

      const rawMachineEventData = item?.rawMachineEventData as RawMachineEventData;
      if (!rawMachineEventData) return null;
      const countingMethod = rawMachineEventData?.machineCountingDetails?.countingMethod;
      if (!countingMethod) return null;
      return (
        <View>
          <Text numberOfLines={1} ellipsizeMode="tail" style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
            {i18n.t('Counting Method')}: {countingMethod}
          </Text>
        </View>
      );
    }

    case 'subrow-startBlock': {
      const rawMachineEventData = item?.rawMachineEventData as RawMachineEventData;
      if (!rawMachineEventData) return null;
      const startBlock = rawMachineEventData?.currentBlockNumberRaw;
      return (
        <View style={{ paddingLeft: scale(5) }}>
          <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
            {i18n.t('Start Block')}: {getStartBlockText(startBlock)}
          </Text>
        </View>
      );
    }

    default:
      console.warn(`Unknown column type: ${column.type}`);
      return null;
  }
};
